import type { Shift } from "../types/Shift";
import { reconcileOrderedJobIds } from "./jobOrdering";

/**
 * New jobId set for one shift after staged edits: `(current ∪ additions) \ removals`.
 * Order is current-first, then any newly-added ids in iteration order; duplicates collapse.
 */
export function mergeShiftJobs(
	current: readonly string[],
	additions: Iterable<string>,
	removals: Iterable<string>,
): string[] {
	const merged = new Set<string>(current);
	for (const id of additions) merged.add(id);
	for (const id of removals) merged.delete(id);
	return Array.from(merged);
}

/**
 * Apply staged additions/removals (each keyed by shift id) to a set of existing shifts, returning
 * a fresh Shift for every one actually touched (referenced by any map). Shifts that no map
 * mentions — or whose merged `jobIds` come out identical to the current ones — are omitted, so
 * only genuinely changed shifts get an update call.
 *
 * `orderedOverrides` carries each touched shift's intended ordered `jobIds` (the Phase-3 staged
 * order, which may still contain removal-staged ids and is reconciled against the merged
 * membership here). A shift present only in `orderedOverrides` is a pure reorder.
 *
 * The returned objects are plain spreads of the source shift with a new `jobIds` (not class
 * instances); the persistence adapter serializes the fields, so the prototype isn't needed.
 */
export function buildShiftJobUpdates(
	shifts: readonly Shift[],
	additions: ReadonlyMap<string, ReadonlySet<string>>,
	removals: ReadonlyMap<string, ReadonlySet<string>>,
	orderedOverrides?: ReadonlyMap<string, readonly string[]>,
): Shift[] {
	const touched = new Set<string>([...additions.keys(), ...removals.keys(), ...(orderedOverrides?.keys() ?? [])]);
	const updates: Shift[] = [];
	for (const shiftId of touched) {
		const shift = shifts.find((s) => s.id === shiftId);
		if (!shift) continue;
		const adds = additions.get(shiftId) ?? new Set<string>();
		const removes = removals.get(shiftId) ?? new Set<string>();
		const membership = mergeShiftJobs(shift.jobIds, adds, removes);
		const override = orderedOverrides?.get(shiftId);
		const jobIds = override ? reconcileOrderedJobIds(override, membership) : membership;
		const unchanged = jobIds.length === shift.jobIds.length && jobIds.every((id, i) => id === shift.jobIds[i]);
		if (unchanged) continue;
		updates.push({ ...shift, jobIds } as Shift);
	}
	return updates;
}
